import type WboTransportProvider from './interfaces/wbo-transport-provider.interface';
import type {
    WboAnnotationDocumentMetadata,
    WboDelta,
    WboFetchResponse,
    WboMsg,
    WboRequestMessage,
    WboServerResponse,
} from './model/wbo-annotation.model';
import type GenericQueue from './util/generic-queue';

/**
 * This class handles the sending and receiving of the operations to / from the WBO server. The details about the
 * transport are delegated to the WboTransportProvider.
 */
export default class WboCommandController {
    private transportProvider: WboTransportProvider;
    private incomingOps: GenericQueue<WboDelta>;
    private readonly boardName: string;

    /**
     * Construct a command controller that relies on a specific transport provider to send commands to the server and
     * receive responses.
     * @param transportProvider Object that handles the actual transport of the messages.
     * @param incomingOps Queue in which this object will write the deltas arriving from the server.
     * @param boardName Name of the board to which this controller is connected.
     */
    constructor(transportProvider: WboTransportProvider, incomingOps: GenericQueue<WboDelta>, boardName: string) {
        this.transportProvider = transportProvider;
        this.incomingOps = incomingOps;
        this.boardName = boardName;
    }

    subscribe(boardName: string, boardMetadata: WboAnnotationDocumentMetadata): void {
        this.transportProvider.subscribe(boardName, boardMetadata);
    }

    broadcast(msg: WboMsg): void {
        this.transportProvider.sendDelta({ opType: 'broadcast', msg });
    }

    removeAllByUser(msg: WboMsg): Promise<WboServerResponse | undefined> {
        return this.sendWithAck({ opType: 'removeAllByUser', msg });
    }

    async fetch(request: WboRequestMessage): Promise<WboFetchResponse> {
        return this.transportProvider.sendRequest(request);
    }

    listenRemoteMessages(): void {
        this.transportProvider.onMessage((delta) => {
            if (delta.msg.board === this.boardName) {
                this.incomingOps.enqueue(delta);
            }
        });
    }

    send(wboDelta: WboDelta): void {
        this.transportProvider.sendDelta(wboDelta);
    }

    sendWithAck(wboDelta: WboDelta): Promise<WboServerResponse | undefined> {
        return this.transportProvider.sendDeltaWithAck(wboDelta);
    }

    close(): void {
        this.transportProvider.close();
    }
}
